import React, { useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { XCircle, RefreshCw, HelpCircle, MessageCircle } from 'lucide-react';
import { trackEvent } from './lib/analytics';

const PaymentFailure = () => {
    const [searchParams] = useSearchParams();
    const reason = searchParams.get('reason');
    const txnid = searchParams.get('txnid');

    // Decode the reason if it looks like it's URL encoded
    const decodedReason = reason ? decodeURIComponent(reason) : 'Payment processing failed';

    useEffect(() => {
        trackEvent('payment_failed', { transaction_id: txnid || undefined, reason: decodedReason });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [txnid]);

    return (
        <div className="min-h-screen p-4 bg-[#F4ECDE] flex items-center justify-center">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
                <div className="flex justify-center mb-6">
                    <div className="rounded-full bg-red-100 p-3">
                        <XCircle className="w-16 h-16 text-red-600" />
                    </div>
                </div>

                <h1 className="text-3xl font-bold text-[#471F21] mb-2">Payment Failed</h1>
                <p className="text-gray-600 mb-6">
                    We couldn't process your payment. No amount was deducted.
                </p>

                <div className="bg-red-50 rounded-xl p-4 mb-8 text-left border border-red-100">
                    <div className="flex items-start">
                        <div className="bg-red-100 rounded-full p-1 mr-3 mt-0.5">
                            <HelpCircle className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                            <h3 className="font-bold text-red-800 text-sm mb-1">Error Details</h3>
                            <p className="text-sm text-red-600 break-words">
                                {decodedReason}
                            </p>
                            {txnid && (
                                <p className="text-xs text-gray-500 mt-2 font-mono">
                                    Ref: {txnid}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="space-y-3">
                    <Link
                        to="/booking"
                        className="block w-full bg-[#471F21] text-white py-3 px-6 rounded-xl font-bold hover:bg-[#2d1415] transition-colors flex items-center justify-center"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Try Booking Again
                    </Link>

                    <a
                        href={`https://wa.me/919398235164?text=${encodeURIComponent(`Hi, my payment for booking ${txnid || ''} failed. Can you please help me confirm it manually?`)}`}
                        className="block w-full bg-[#25D366] text-white py-3 px-6 rounded-xl font-bold hover:bg-[#128C7E] transition-colors flex items-center justify-center"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Contact Admin via WhatsApp
                    </a>

                    <Link
                        to="/"
                        className="block w-full text-gray-600 hover:text-[#471F21] py-3 text-sm font-medium transition-colors"
                    >
                        Return to Home
                    </Link>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100">
                    <div className="bg-amber-50 rounded-lg p-4 mb-4 border border-amber-100">
                        <p className="text-sm text-amber-800 font-medium">
                            Don't worry! Your slot is held for **1 hour** to allow for manual confirmation. You can contact us via WhatsApp to recover your booking.
                        </p>
                    </div>
                    <p className="text-sm text-gray-500 mb-2">Need help with your booking?</p>
                    <a href="tel:+919398235164" className="text-[#471F21] font-bold hover:underline">
                        Contact Support: +91 93982 35164
                    </a>
                </div>
            </div>
        </div>
    );
};

export default PaymentFailure;
